import { type SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "semantic-ui-react";
import { Checkbox } from "../../../../../Application/layout/Checkbox/Checkbox";
import {
  type MultipleChoiceQuestion,
  type OtherAnswer,
  type QuestionAnswer,
  type SingleChoiceQuestion,
} from "../../../../types";

import styles from "./Answers.module.scss";

interface Props {
  question: MultipleChoiceQuestion | SingleChoiceQuestion;
  setSelectedAnswers?: (answers: QuestionAnswer[]) => void;
  selectedAnswers: QuestionAnswer[];
  otherAnswer: OtherAnswer;
  setOtherAnswer: React.Dispatch<SetStateAction<OtherAnswer>>;
}

export const ChoiseAnswers: React.FC<Props> = ({
  question,
  setSelectedAnswers,
  selectedAnswers,
  otherAnswer,
  setOtherAnswer,
}) => {
  const { t } = useTranslation("assets");

  const isQuestionSingleselect = question.type === "SingleChoiceTextQuestion";

  const updateCheckbox = useCallback(
    (idx: number) => {
      const temp = selectedAnswers.slice();
      temp[idx] = !temp[idx];
      setSelectedAnswers?.(temp);
    },
    [selectedAnswers, setSelectedAnswers],
  );
  const updateRadio = useCallback(
    (idx: number) => {
      const temp = [];
      temp[idx] = true;
      setSelectedAnswers?.(temp);
      setOtherAnswer(prev => ({ ...prev, checked: false }));
    },
    [setOtherAnswer, setSelectedAnswers],
  );

  const onOtherValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherAnswer(prev => (prev ? { ...prev, value: e.target.value } : { checked: true, value: e.target.value }));
  };
  const onOtherChecked = useCallback(() => {
    if (isQuestionSingleselect) {
      setSelectedAnswers?.([]);
    }
    setOtherAnswer(prev => ({ ...prev, checked: !prev?.checked }));
  }, [isQuestionSingleselect, setOtherAnswer, setSelectedAnswers]);

  const AnswerComponent = isQuestionSingleselect ? Radio : Checkbox;
  const answerUpdater = isQuestionSingleselect ? updateRadio : updateCheckbox;

  const includeUserTextField = question.otherAnswerOptions.includeUserTextField;

  return (
    <div className={styles.answers}>
      {question.answers.map((answer, idx) => {
        const selectedAnswer = !!selectedAnswers[idx];

        return (
          <div className={styles.answer} key={answer.id}>
            <AnswerComponent
              className={styles.item}
              label={answer.answer}
              aria-label={answer.answer}
              checked={selectedAnswer}
              name={`answer${idx}`}
              onChange={() => answerUpdater(idx)}
            />
          </div>
        );
      })}
      {question.otherAnswerOptions.includeOtherAsAnswer && (
        <div className={styles.item}>
          <AnswerComponent
            aria-label="Other"
            checked={!!otherAnswer?.checked}
            name={`answer${question.answers.length}`}
            onChange={onOtherChecked}
            label={includeUserTextField ? undefined : t("assessments.other")}
          />
          {includeUserTextField && (
            <input
              className={styles.text}
              placeholder={t("assessments.other")}
              onChange={onOtherValueChanged}
              value={otherAnswer?.value ?? ""}
              disabled={!otherAnswer?.checked}
            />
          )}
        </div>
      )}
    </div>
  );
};
